import { useState } from 'react';
import {
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  Estate_GetEstateByIdDocument,
  Estate_GetEstatesByPortalIdDocument,
  Estate_GetEstatesByPortalIdQuery,
  EstateWorkItem_GetEstateWorkItemsCountsByPortalIdDocument,
  TaskCredit_GetCurrentTaskCreditDocument,
  useEstate_MakePaymentMutation,
  useEstateResultItem_MakePaymentMutation,
  useEstateTaskInstance_MakePaymentMutation,
  useFeatureSubscription_GetFeatureSubscriptionTypeQuery,
  useFeatureSubscription_GetPortalSeatSubscriptionProductQuery,
  useFeatureSubscription_GetPortalSeatSubscriptionQuery,
} from 'queries';
import { useAnalytics } from 'use-analytics';

import FormInputErrorMessage from 'common/form-input-error-message';
import Spinner from 'common/spinner';
import { AnalyticsEvent } from 'utils/enum';
import useCurrentUser from 'utils/useCurrentUser';

type Props = {
  allowPaymentQuantity?: boolean;
  bodyContent?: JSX.Element;
  estateId?: string;
  estateResultItemId?: string;
  estateTaskInstanceId?: string;
  hasPayment: boolean;
  formName?: string;
  // eslint-disable-next-line @typescript-eslint/ban-types
  handlePaymentComplete: Function;
  portalId: string;
  title?: string;
};

const useStyles = makeStyles(() => ({
  paymentWrapper: {
    textAlign: 'left',
  },
  couponInputWrapper: {
    paddingBottom: 0,
  },
  chargeMessage: {
    paddingTop: '30px',
  },
  bodyContentWrapper: {
    paddingTop: 10,
    paddingBottom: 10,
  },
  payment: {
    backgroundColor: 'white',
    padding: '15px',
    paddingTop: '5px',
    marginTop: '10px',
  },
  paymentInstruction: {
    paddingBottom: 10,
  },
  paymentQuantityWrapper: {
    paddingTop: 5,
    paddingBottom: 5,
  },
  paymentActionIndicator: {
    textAlign: 'center',
  },
  quantitySelectForm: {
    position: 'relative',
  },
  quantitySelect: {
    backgroundColor: 'white',
  },
  quantitySelectLabel: {
    verticalAlign: 'middle',
    paddingLeft: 5,
    paddingTop: 10,
  },
  labelContextFieldsWrapper: {
    paddingTop: 10,
    paddingBottom: 10,
  },
  labelContextField: {
    width: '100%',
    paddingTop: 1,
    paddingBottom: 1,
  },
}));

export default function PaymentForm({
  allowPaymentQuantity,
  bodyContent,
  estateId,
  estateResultItemId,
  estateTaskInstanceId,
  hasPayment,
  formName,
  handlePaymentComplete,
  portalId,
}: Props) {
  const classes = useStyles();
  const analytics = useAnalytics();
  const [paymentError, setPaymentError] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [labelContextMap, setLabelContextMap] = useState<{ [key: number]: string }>({});

  const currentUser = useCurrentUser();

  const { data: featureSubscriptionTypeData, loading: featureSubscriptionTypeLoading } =
    useFeatureSubscription_GetFeatureSubscriptionTypeQuery({
      variables: {
        portalId,
      },
      onError: (e) => setPaymentError(e.message),
    });

  const { data: seatSubscriptionData, loading: seatSubscriptionLoading } =
    useFeatureSubscription_GetPortalSeatSubscriptionQuery({
      variables: {
        portalId,
      },
      onError: (e) => setPaymentError(e.message),
    });

  const { data: seatSubscriptionProductData, loading: seatSubscriptionProductLoading } =
    useFeatureSubscription_GetPortalSeatSubscriptionProductQuery({
      variables: {
        portalId,
      },
      onError: (e) => setPaymentError(e.message),
    });

  const [estateMakePaymentMutation, { loading: estateMakePaymentMutationExecuting }] =
    useEstate_MakePaymentMutation({
      refetchQueries: [
        {
          query: TaskCredit_GetCurrentTaskCreditDocument,
          variables: { portalId },
        },
        {
          query: Estate_GetEstateByIdDocument,
          variables: { id: estateId },
        },
      ],
      onCompleted: (data) => {
        analytics.track(AnalyticsEvent.TASK_ASSIGNED, {
          'Task Type': 'Trusuite Search',
          'Estate ID': data.data.id,
          'Estate Name': data.data.estateName,
          'Estate Type': data.data.type,
        });

        handlePaymentComplete(data.data);
      },
      onError: (e) => setPaymentError(e.message),
    });

  const [
    estateResultItemMakePaymentMutation,
    { loading: estateResultItemMakePaymentMutationExecuting },
  ] = useEstateResultItem_MakePaymentMutation({
    refetchQueries: [
      {
        query: TaskCredit_GetCurrentTaskCreditDocument,
        variables: { portalId },
      },
    ],
    onCompleted: (data) => {
      analytics.track(AnalyticsEvent.TASK_ASSIGNED, {
        'Task Type': 'Estate Result Item',
        'Estate Result Item ID': data.data.id,
        'Estate Result Item Title': data.data.title,
      });

      handlePaymentComplete(data.data);
    },
    onError: (e) => setPaymentError(e.message),
  });

  const [
    estateTaskInstanceMakePaymentMutation,
    { loading: estateTaskInstanceMakePaymentMutationExecuting },
  ] = useEstateTaskInstance_MakePaymentMutation({
    refetchQueries: [
      {
        query: TaskCredit_GetCurrentTaskCreditDocument,
        variables: { portalId },
      },
      {
        query: EstateWorkItem_GetEstateWorkItemsCountsByPortalIdDocument,
        variables: { portalId },
      },
    ],
    onCompleted: ({ data: estateTaskInstances }) => {
      for (const estateTaskInstance of estateTaskInstances) {
        const label = estateTaskInstance.labelContext
          ? `${estateTaskInstance.label} (${estateTaskInstance.labelContext})`
          : estateTaskInstance.label;

        analytics.track(AnalyticsEvent.TASK_ASSIGNED, {
          'Task Type': 'Estate Task Instance',
          'Estate Task Instance ID': estateTaskInstance.id,
          'Estate Task Instance Label': label,
          'Estate ID': estateTaskInstance.estateId,
          'Credits Used': 1,
        });
      }

      handlePaymentComplete(estateTaskInstances);
    },
    update: (store) => {
      const estateData = store.readQuery<Estate_GetEstatesByPortalIdQuery>({
        query: Estate_GetEstatesByPortalIdDocument,
        variables: { input: { portalId } },
      });

      if (estateData) {
        const estates = estateData ? [...estateData.data] : [];

        store.writeQuery<Estate_GetEstatesByPortalIdQuery>({
          query: Estate_GetEstatesByPortalIdDocument,
          data: {
            data: estates,
          },
          variables: { input: { portalId } },
        });
      }
    },
    onError: (e) => setPaymentError(e.message),
  });

  if (!featureSubscriptionTypeData && featureSubscriptionTypeLoading) {
    return <Spinner />;
  }

  if (!seatSubscriptionData && seatSubscriptionLoading) {
    return <Spinner />;
  }

  if (!seatSubscriptionProductData && seatSubscriptionProductLoading) {
    return <Spinner />;
  }

  if (
    estateMakePaymentMutationExecuting ||
    estateResultItemMakePaymentMutationExecuting ||
    estateTaskInstanceMakePaymentMutationExecuting
  ) {
    return <Spinner />;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const submitForm = async (event: any) => {
    if (estateResultItemId && !hasPayment) {
      estateResultItemMakePaymentMutation({
        variables: {
          input: {
            id: estateResultItemId,
            portalId,
            userId: currentUser?.id || '',
            couponId: '',
          },
        },
        onError: (e) => setPaymentError(e.message),
      });
    } else if (estateTaskInstanceId && !hasPayment) {
      const labelContexts: string[] = [];
      for (let i = 0; i < quantity; i++) {
        labelContexts.push(labelContextMap[i] || '');
      }

      estateTaskInstanceMakePaymentMutation({
        variables: {
          input: {
            id: estateTaskInstanceId,
            portalId,
            userId: currentUser?.id || '',
            couponId: '',
            quantity: quantity || 1,
            labelContexts,
          },
        },
        onError: (e) => setPaymentError(e.message),
      });
    } else if (!hasPayment) {
      estateMakePaymentMutation({
        variables: {
          id: estateId || '',
          portalId: portalId,
          userId: currentUser?.id || '',
          couponId: '',
        },
        onError: (e) => setPaymentError(e.message),
      });
    } else {
      // move forward, no payment required
      handlePaymentComplete(null);
    }

    event.preventDefault();
  };

  return (
    <form id={formName} onSubmit={submitForm}>
      {hasPayment ? (
        <Typography variant="h5">Assignment is complete for this item.</Typography>
      ) : (
        <div className={classes.paymentWrapper}>
          <div style={{ minHeight: '140px' }}>
            {bodyContent && <div className={classes.bodyContentWrapper}>{bodyContent}</div>}
            {allowPaymentQuantity && (
              <div className={classes.paymentQuantityWrapper}>
                <FormControl className={classes.quantitySelectForm} variant="outlined" size="small">
                  <Select
                    className={classes.quantitySelect}
                    labelId="quantity"
                    value={quantity}
                    onChange={(e: SelectChangeEvent<number>) => {
                      const newQuantity = e.target.value as number;

                      // keep the state of existing label context inputs if the quantity would still show the input field
                      const newLabelContextMap: {
                        [key: number]: string;
                      } = {};
                      for (var i = 0; i < newQuantity; i++) {
                        newLabelContextMap[i] = labelContextMap[i];
                      }

                      setLabelContextMap(newLabelContextMap);
                      setQuantity(newQuantity);
                    }}
                  >
                    {[...Array(10).keys()].map((i) => {
                      return <MenuItem value={i + 1}>{i + 1}</MenuItem>;
                    })}
                  </Select>
                </FormControl>
                <span className={classes.quantitySelectLabel}>Select quantity</span>
              </div>
            )}
            {estateTaskInstanceId && (
              <div className={classes.labelContextFieldsWrapper}>
                <span>{quantity > 1 ? 'Create Task Labels' : 'OPTIONAL: Create a Task Label'}</span>
                {[...Array(quantity).keys()].map((i) => {
                  return (
                    <TextField
                      key={'labelContext' + i}
                      className={classes.labelContextField}
                      name="labelContext"
                      variant="outlined"
                      placeholder="Enter Institution, Account, or Asset name"
                      size="small"
                      value={labelContextMap[i] || ''}
                      inputProps={{ width: '100%', maxLength: 50 }}
                      onChange={(e) => {
                        const newLabelContextMap = { ...labelContextMap };
                        newLabelContextMap[i] = e.target.value;
                        setLabelContextMap(newLabelContextMap);
                      }}
                    />
                  );
                })}
              </div>
            )}
            <FormInputErrorMessage error={paymentError} />
          </div>
        </div>
      )}
    </form>
  );
}
